import { useApi } from '@/common/ApiHelper';
import type { AssessmentHeatmapEntry } from '@/components/analysis/types';
import { useLoginStore } from '@/login/stores/useLoginStore';
import { ConditionOperator, LogicalOperator } from '@sysarb/sysarb-api/models/common/selection/Selection';
import type { Guid } from 'guid-typescript';
import type { QueryOptions } from '@tanstack/vue-query';
import { queryOptions } from '@tanstack/vue-query';
import { resolveSectionManagerNodeIds } from './organisationNodeHelpers';

type SalaryReviewAssessmentsOptions = {
    queries: {
        /** Salary-criteria heatmap entries for the section manager's node tree, scoped to a salary review. */
        getSalaryReviewAssessments: (salaryReviewId: Guid | string | null) => QueryOptions<AssessmentHeatmapEntry[], Error>;
    };
};

export const salaryReviewAssessments: SalaryReviewAssessmentsOptions = {
    queries: {
        getSalaryReviewAssessments: (salaryReviewId: Guid | string | null): QueryOptions<AssessmentHeatmapEntry[], Error> => {
            const { currentOrganisationNodeId } = useLoginStore();

            return queryOptions<AssessmentHeatmapEntry[], Error>({
                queryKey: ['section-manager', 'salary-review-assessments', currentOrganisationNodeId, salaryReviewId?.toString() ?? null],
                queryFn: async (): Promise<AssessmentHeatmapEntry[]> => {
                    const api = useApi();
                    const nodeIds = await resolveSectionManagerNodeIds(api, currentOrganisationNodeId);
                    if (nodeIds.length === 0) return [];

                    const result = await api.salaryReviewAssessment.getBySelection({
                        Condition: {
                            LogicalOperator: LogicalOperator.And,
                            Conditions: [
                                {
                                    PropertyName: 'OrganisationNode_ID',
                                    ConditionOperator: ConditionOperator.In,
                                    Value: nodeIds,
                                },
                                {
                                    PropertyName: 'SalaryReview_ID',
                                    ConditionOperator: ConditionOperator.EqualTo,
                                    Value: salaryReviewId,
                                },
                            ],
                        },
                    });

                    const entries: AssessmentHeatmapEntry[] = [];
                    for (const assessment of result) {
                        const assessmentValue = assessment.assessmentValue;
                        if (assessmentValue?.value == null) continue;
                        entries.push({
                            criteriaId: assessment.salaryReviewSalaryCriteria.id.toString(),
                            criteriaName: assessment.salaryReviewSalaryCriteria.name,
                            assessmentValue: assessmentValue.value,
                            assessmentTitle: assessmentValue.title,
                            organisationNodeId: assessment.organisationNode.id.toString(),
                            salaryReviewEmploymentId: assessment.salaryReviewEmployment.id.toString(),
                        });
                    }
                    return entries;
                },
                enabled: () => salaryReviewId != null && currentOrganisationNodeId != null,
            });
        },
    },
};
